import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { parentApi, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Icon from "../../components/Icon";

const STAT_META = [
  { key: "families", label: "Families", icon: "users", color: "text-sky-300" },
  { key: "active", label: "Active plans", icon: "crown", color: "text-violet-300" },
  { key: "trialing", label: "On trial", icon: "clock", color: "text-amber-300" },
  { key: "suspended", label: "Suspended", icon: "ban", color: "text-rose-300" },
  { key: "kids", label: "Kids", icon: "smile", color: "text-fuchsia-300" },
];

function Badge({ tone, children, testid }) {
  const tones = {
    demo: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    active: "bg-violet-400/15 text-violet-300 border-violet-400/30",
    trial: "bg-sky-400/15 text-sky-300 border-sky-400/30",
    suspended: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    comped: "bg-fuchsia-400/15 text-fuchsia-300 border-fuchsia-400/30",
    idle: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${tones[tone] || tones.idle}`}>{children}</span>;
}

function BarChart({ data, valueKey, color = "#A78BFA", testid }) {
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  return (
    <div data-testid={testid} className="flex items-end gap-[2px] h-28">
      {data.map((d) => {
        const h = (d[valueKey] / max) * 100;
        return <div key={d.date} className="flex-1 rounded-t-sm" style={{ height: `${Math.max(3, h)}%`, background: color, opacity: d[valueKey] ? 1 : 0.2 }} title={`${d.date}: ${d[valueKey]}`} />;
      })}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f, ins] = await Promise.all([parentApi.get("/admin/stats"), parentApi.get("/admin/families"), parentApi.get("/admin/insights")]);
      setStats(s.data);
      setFamilies(f.data.families);
      setInsights(ins.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return families;
    return families.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.owner_email || "").toLowerCase().includes(q) ||
      (r.access_code || "").toLowerCase().includes(q));
  }, [families, query]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const act = async (id, fn) => {
    setBusyId(id);
    try { await fn(); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusyId(null); }
  };

  const toggleSuspend = (r) => act(r.id, async () => {
    await parentApi.post(`/admin/families/${r.id}/suspend`, { suspended: !r.is_suspended });
    toast.success(r.is_suspended ? "Family reactivated" : "Family suspended");
  });

  const toggleComp = (r) => {
    const isPlus = r.subscription_status === "active" || r.comped;
    return act(r.id, async () => {
      await parentApi.post(`/admin/families/${r.id}/comp`, { grant: !isPlus, plan: "annual" });
      toast.success(isPlus ? "Plus revoked" : "Free Plus granted 🎁");
    });
  };

  const doDelete = () => {
    const r = confirmDel;
    setConfirmDel(null);
    return act(r.id, async () => {
      await parentApi.delete(`/admin/families/${r.id}`);
      toast.success(`Deleted ${r.name}`);
    });
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-violet-500/20 text-violet-300"><Icon name="shield-check" width={20} height={20} /></span>
            <div>
              <div className="font-heading font-black text-lg leading-none">ChoreMouse Admin</div>
              <div className="text-xs text-slate-400 mt-0.5">{user?.email}</div>
            </div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-3 py-2 text-sm font-bold hover:bg-white/5 transition-colors">
            <Icon name="log-out" width={16} height={16} /> Log out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6 space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {STAT_META.map((s) => (
            <div key={s.key} data-testid={`admin-stat-${s.key}`} className="rounded-2xl bg-slate-800/60 border border-white/10 p-4">
              <Icon name={s.icon} className={`mb-2 ${s.color}`} width={20} height={20} />
              <div className="font-heading font-black text-2xl">{stats ? stats[s.key] : "—"}</div>
              <div className="text-xs font-semibold text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        {insights && (
          <div className="space-y-4" data-testid="admin-insights">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-2xl bg-slate-800/60 border border-white/10 p-4"><div className="text-xs font-semibold text-slate-400">MRR (est.)</div><div className="font-heading font-black text-2xl text-violet-300" data-testid="insight-mrr">${insights.mrr.toFixed(2)}</div></div>
              <div className="rounded-2xl bg-slate-800/60 border border-white/10 p-4"><div className="text-xs font-semibold text-slate-400">Collected (test)</div><div className="font-heading font-black text-2xl text-sky-300">${insights.total_collected.toFixed(2)}</div></div>
              <div className="rounded-2xl bg-slate-800/60 border border-white/10 p-4"><div className="text-xs font-semibold text-slate-400">Paid plans</div><div className="font-heading font-black text-2xl">{insights.plan_mix.monthly + insights.plan_mix.annual}</div><div className="text-[11px] text-slate-500">{insights.plan_mix.monthly} mo · {insights.plan_mix.annual} yr</div></div>
              <div className="rounded-2xl bg-slate-800/60 border border-white/10 p-4"><div className="text-xs font-semibold text-slate-400">Comped</div><div className="font-heading font-black text-2xl text-fuchsia-300">{insights.plan_mix.comped}</div></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="rounded-2xl bg-slate-800/50 border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3"><span className="font-heading font-bold text-sm">Signups</span><span className="text-xs text-slate-400">last {insights.days} days · {insights.signups.reduce((a, b) => a + b.count, 0)} new</span></div>
                <BarChart data={insights.signups} valueKey="count" color="#38BDF8" testid="chart-signups" />
                <div className="flex justify-between text-[10px] text-slate-500 mt-1"><span>{insights.signups[0]?.date.slice(5)}</span><span>{insights.signups[insights.signups.length - 1]?.date.slice(5)}</span></div>
              </div>
              <div className="rounded-2xl bg-slate-800/50 border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3"><span className="font-heading font-bold text-sm">Revenue</span><span className="text-xs text-slate-400">last {insights.days} days</span></div>
                <BarChart data={insights.revenue} valueKey="amount" color="#A78BFA" testid="chart-revenue" />
                <div className="flex justify-between text-[10px] text-slate-500 mt-1"><span>{insights.revenue[0]?.date.slice(5)}</span><span>{insights.revenue[insights.revenue.length - 1]?.date.slice(5)}</span></div>
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" width={18} height={18} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search"
            placeholder="Search by family name, owner email or code…"
            className="w-full rounded-2xl bg-slate-800/60 border border-white/10 pl-10 pr-4 py-3 font-medium text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-violet-400/50" />
        </div>

        <div className="text-sm font-semibold text-slate-400">{filtered.length} {filtered.length === 1 ? "family" : "families"}</div>

        {loading ? (
          <div className="grid place-items-center py-20"><div className="w-10 h-10 rounded-full border-4 border-white/20 border-t-violet-400 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => {
              const isPlus = r.subscription_status === "active" || r.comped;
              return (
                <div key={r.id} data-testid={`family-card-${r.id}`} className="rounded-2xl bg-slate-800/50 border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-extrabold text-base truncate">{r.name || "Unnamed family"}</span>
                      {r.is_demo && <Badge tone="demo">Demo</Badge>}
                      {r.is_suspended && <Badge tone="suspended" testid={`badge-suspended-${r.id}`}>Suspended</Badge>}
                      {r.comped ? <Badge tone="comped">Comped</Badge> : isPlus ? <Badge tone="active">Plus</Badge> : r.subscription_status === "trial" ? <Badge tone="trial">Trial</Badge> : <Badge tone="idle">{r.subscription_status || "inactive"}</Badge>}
                    </div>
                    <div className="text-sm text-slate-400 mt-1 truncate">{r.owner_email || "no owner"}</div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      <span>Code <span className="text-slate-300 font-bold">{r.access_code}</span></span>
                      <span>{r.members_count} grown-up{r.members_count === 1 ? "" : "s"}</span>
                      <span>{r.kids_count} kid{r.kids_count === 1 ? "" : "s"}</span>
                      {r.created_at && <span>Joined {new Date(r.created_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button disabled={busyId === r.id || r.is_demo} onClick={() => toggleComp(r)} data-testid={`comp-${r.id}`}
                      className="rounded-xl border border-white/15 px-3 py-2 text-sm font-bold hover:bg-white/5 disabled:opacity-40 transition-colors">
                      {isPlus ? "Revoke Plus" : "Grant Plus"}
                    </button>
                    <button disabled={busyId === r.id || r.is_demo} onClick={() => toggleSuspend(r)} data-testid={`suspend-${r.id}`}
                      className={`rounded-xl px-3 py-2 text-sm font-bold disabled:opacity-40 transition-colors ${r.is_suspended ? "bg-violet-500/20 text-violet-300 hover:bg-violet-500/30" : "border border-amber-400/30 text-amber-300 hover:bg-amber-400/10"}`}>
                      {r.is_suspended ? "Reactivate" : "Suspend"}
                    </button>
                    <button disabled={busyId === r.id || r.is_demo} onClick={() => setConfirmDel(r)} data-testid={`delete-${r.id}`}
                      className="rounded-xl border border-rose-500/30 text-rose-300 px-3 py-2 text-sm font-bold hover:bg-rose-500/10 disabled:opacity-40 transition-colors">
                      <Icon name="trash-2" width={16} height={16} />
                    </button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="text-center text-slate-500 py-16 font-semibold">No families match your search.</div>}
          </div>
        )}
      </main>

      {confirmDel && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 px-5" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-sm rounded-3xl bg-slate-800 border border-white/10 p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-delete-modal">
            <div className="flex items-center gap-2 mb-2 text-rose-300"><Icon name="triangle-alert" width={22} height={22} /><span className="font-heading font-black text-lg text-slate-100">Delete family?</span></div>
            <p className="text-sm text-slate-400 mb-5">This permanently deletes <b className="text-slate-200">{confirmDel.name}</b> — all grown-ups, kids, chores and rewards. This can't be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDel(null)} className="flex-1 rounded-xl border border-white/15 px-3 py-2.5 font-bold hover:bg-white/5">Cancel</button>
              <button onClick={doDelete} data-testid="confirm-delete-btn" className="flex-1 rounded-xl bg-rose-500 text-white px-3 py-2.5 font-bold hover:bg-rose-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
